import './Cursor.css'

import { useEffect, useRef } from "react";
import * as THREE from 'three';
import gsap from 'gsap';
import { CloneObjet } from "../components/three/ScenarioManager";
import { EXRLoader } from "three/examples/jsm/Addons.js";
import { group } from './three/Scene';

const Cursor = ({ isModelLoaded, scenarioStep, isWorkInlineContainerVisible }) => {
    const cursorWebglRef = useRef(null);

    const sceneRef = useRef(null);
    const groupRef = useRef(null);

    let exrCubeRenderTarget;

    const pmremGeneratorRef = useRef(null);

    useEffect(() => {
        sceneRef.current = new THREE.Scene();
        groupRef.current = new THREE.Group();
        sceneRef.current.add(groupRef.current);
        //* 기본 세팅
        let targetX = 0, targetY = 0;

        //* 카메라
        // 👇 수정된 카메라 설정 코드
        const frustumSize = 1; // 기준 고정 크기
        let aspect = window.innerWidth / window.innerHeight;

        // const height = frustumSize;
        // const width = height * aspect;

        // camera.left = -width / 2;
        // camera.right = width / 2;
        // camera.top = height / 2;
        // camera.bottom = -height / 2;

        const camera = new THREE.OrthographicCamera(
            -frustumSize * aspect / 2,
            frustumSize * aspect / 2,
            frustumSize / 2,
            -frustumSize / 2,
            0.1,
            100
        );
        sceneRef.current.add(camera);
        camera.position.set(0, 0, 1);

        // * 렌더러
        const renderer = new THREE.WebGLRenderer({
            canvas: cursorWebglRef.current,
            alpha: true,
            antialias: true
        });
        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.setClearColor(0xffffff, 0);
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.0;

        pmremGeneratorRef.current = new THREE.PMREMGenerator(renderer);
        pmremGeneratorRef.current.compileEquirectangularShader();

        //* Light 추가
        const light = new THREE.DirectionalLight(0xffffff, 0.5);
        light.position.set(0.1, 1, 0.3);
        sceneRef.current.add(light);

        const ambientLight = new THREE.AmbientLight(0x0055aa);
        sceneRef.current.add(ambientLight);

        let orbitAngle = 0;
        const angleSpeed = 0.01;
        const radius = 0.015;

        //* 루프
        const loop = () => {
            requestAnimationFrame(loop);

            orbitAngle += angleSpeed;

            //* 마우스 따라 다니기
            for (var i = 0; i < groupRef.current.children.length; i++) {
                const child = groupRef.current.children[i];

                if (child) {
                    child.position.x = THREE.MathUtils.lerp(child.position.x, targetX, 0.1 - i * 0.01);
                    child.position.y = THREE.MathUtils.lerp(child.position.y, targetY, 0.1 - i * 0.01);

                    child.children[0].position.x = THREE.MathUtils.lerp(
                        child.children[0].position.x,
                        Math.cos(orbitAngle + Math.PI * 2 / groupRef.current.children.length * i) * radius, 0.1);
                    child.children[0].position.y = THREE.MathUtils.lerp(
                        child.children[0].position.y,
                        Math.sin(orbitAngle + Math.PI * 2 / groupRef.current.children.length * i) * radius, 0.1);
                }
            }
            renderer.render(sceneRef.current, camera);
        };
        loop();

        // resize 대응
        // 👇 resize 시 카메라 view를 비율 무시하고 균일하게 유지
        const handleResize = () => {
            aspect = window.innerWidth / window.innerHeight;
            const height = frustumSize;
            const width = height * aspect;

            camera.left = -width / 2;
            camera.right = width / 2;
            camera.top = height / 2;
            camera.bottom = -height / 2;

            camera.updateProjectionMatrix();
            renderer.setSize(window.innerWidth, window.innerHeight);
        };
        window.addEventListener('resize', handleResize);

        const cursorMove = (e) => {
            const mouseX = (e.clientX / window.innerWidth) * 2 - 1;
            const mouseY = -(e.clientY / window.innerHeight) * 2 + 1;

            //* 마우스에 우측하단 0.03, 0.06
            targetX = (mouseX + 0.03) * (frustumSize * aspect) / 2;
            targetY = (mouseY - 0.06) * frustumSize / 2;
        };
        window.addEventListener('mousemove', cursorMove);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('mousemove', cursorMove);
            renderer.dispose();
        };
    }, []);

    useEffect(() => {
        if (!isModelLoaded || scenarioStep !== 21) return;

        //* 오브젝트 로딩
        let objet;

        const exrLoader = new EXRLoader().load(
            `${process.env.PUBLIC_URL}/data/hdri/iw_hdri_10.exr`,
            (texture) => {
                exrCubeRenderTarget = pmremGeneratorRef.current.fromEquirectangular(texture);

                const group = new THREE.Group();

                objet = CloneObjet('objet');
                if (objet) {
                    objet.scale.set(0.015, 0.015, 0.015);
                    objet.position.set(0, 0, 0);
                    objet.traverse(child => {
                        if (child.isMesh) {
                            child.material.envMap = exrCubeRenderTarget.texture;
                            child.material.envMapIntensity = 1.2;

                            child.material.onBeforeCompile = (shader) => {
                                shader.fragmentShader = shader.fragmentShader.replace(
                                    '#include <map_fragment>',
                                    `
                                         #ifdef USE_MAP
                                             vec4 texColor = texture2D(map, vMapUv);
                                             diffuseColor.rgb = mix(vec3(1.0) - diffuseColor.rgb, diffuseColor.rgb, texColor.r);
                                         #endif
                                         `
                                );
                            };
                            child.material.needsUpdate = true;
                        }
                    })
                    // finalObjet.visible = true;
                    console.log('final Objet', objet);
                    group.add(objet);
                    groupRef.current.add(group);

                    // 자전 애니메이션 (y, z축 회전)
                    gsap.to(objet.rotation, {
                        y: '+=6.283', // 2 * Math.PI (360도)
                        z: '+=6.283',
                        duration: 4,
                        repeat: -1,
                        ease: 'none'
                    });

                    if (groupRef.current.children.length > 5) {
                        console.log('⚠️ 5개를 넘겼어 ! 한 개를 삭제할꺼야')
                        const child = groupRef.current.children[0];
                        gsap.to(child.children[0].scale, {
                            x: 0,
                            y: 0,
                            z: 0,
                            duration: 0.3,
                            onComplete: () => {
                                groupRef.current.remove(child);
                                // child.dispose();
                            }
                        })
                    }
                }
                texture.dispose();
            }
        )
    }, [isModelLoaded, scenarioStep]);

    useEffect(() => {
        if (isWorkInlineContainerVisible) {
            groupRef.current.visible = false;
        }
        else {
            groupRef.current.visible = true;
        }

    }, [isWorkInlineContainerVisible]);

    return (
        <>
            <div className="cursor-wrap">
                <canvas
                    ref={cursorWebglRef}
                    className="cursor-webgl"
                />
            </div>
        </>
    );
};

export default Cursor;